import { useMemo, useRef, type JSX } from "react";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { useAtom, useSetAtom } from "jotai";
import { z } from "zod";
import { graphql } from "@repo/graphql-types";
import { Page } from "@/components/ui/layout/page/page";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { WelcomeMeetingFooter } from "@/components/onboarding/welcome-meeting-footer";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { LocationCardSelect } from "@/components/ui/select-input/location-card-select";
import {
  CardSelectDescription,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useGraphQL } from "@/hooks/use-graphql";
import type {
  UnidadesConsultingConfig,
  UnidadesRemovidasConfig,
} from "@/lib/launchdarkly";
import { welcomeMeetingAtom } from "@/lib/atoms/welcome-meeting-atom";
import { consultingAtom } from "@/lib/atoms/consulting-atom";
import { parseConsultingAnswers } from "@/lib/consulting-metadata";

const welcomeMeetingLocationSearchSchema = z.object({
  action: z.string().optional(),
});

// Respostas da consultoria já enviadas pelo membro, para reaproveitar no
// reagendamento (escopadas ao próprio usuário pela permission da tabela).
const ConsultingPreviousAnswersQuery = graphql(`
  query ConsultingPreviousAnswersQuery {
    onboarding_visit_metadata(order_by: { createdAt: desc }, limit: 1) {
      metadata
    }
  }
`);

const WelcomeMeetingUnitsMapQuery = graphql(`
  query WelcomeMeetingUnitsMapQuery {
    unidades {
      codUnidade
      nomeLimpo
      logradouro
      complemento
      bairro
      Cidade {
        nome
        UF {
          siglaUF
        }
      }
    }
  }
`);

const WelcomeMeetingUnitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [form, setForm] = useAtom(welcomeMeetingAtom);
  const setConsulting = useSetAtom(consultingAtom);

  // Captura o valor na montagem: o selo só é revelado quando o membro VOLTA
  // para a tela (re-mount), não ao avançar a partir dela — evita o flash do
  // selo durante o re-render que antecede a navegação.
  const hadEnteredConsultingOnMount = useRef(form.hasEnteredConsulting).current;

  const { data } = useGraphQL(WelcomeMeetingUnitsMapQuery);

  // Só busca as respostas anteriores quando é reagendamento (para reaproveitá-las).
  const { data: priorConsultingData } = useGraphQL(
    ConsultingPreviousAnswersQuery,
    undefined,
    { enabled: form.isRescheduling },
  );

  const { flags } = useRouteContext({ from: "__root__" });

  // Só a tela de seleção de unidade direciona para o fluxo de consultoria, então
  // a regra de elegibilidade vive aqui. Acesso defensivo às flags: o LaunchDarkly
  // pode não enviar a config de unidades, então tratamos a ausência como vazio.
  const { isFlowEnabled, consultingUnidades } = useMemo(() => {
    const enabled = Boolean(flags["ativa-fluxo-de-agendamento-de-consultoria"]);
    const unidadesConfig = flags["unidades-com-agendamento-de-consultoria-ativo"] as
      | UnidadesConsultingConfig
      | undefined;

    return { isFlowEnabled: enabled, consultingUnidades: unidadesConfig?.unidades ?? [] };
  }, [flags]);

  const excludedUnitIds = useMemo(() => {
    const removidasConfig = flags["unidades-removidas-do-agendamento-de-onboarding"] as
      | UnidadesRemovidasConfig
      | undefined;

    return removidasConfig?.unidades ?? [];
  }, [flags]);

  const isConsultingEligible =
    isFlowEnabled &&
    form.selectedLocationCode > 0 &&
    consultingUnidades.includes(form.selectedLocationCode);

  // Indica as unidades com consultoria na listagem apenas quando o membro já está
  // no contexto de consultoria: reagendando ou trocando a partir de uma unidade
  // elegível (mantém o disfarce no fluxo inicial).
  const consultingUnitIds = useMemo(() => {
    if (!isFlowEnabled) return [];

    const isConsultingContext = form.isRescheduling || hadEnteredConsultingOnMount;

    return isConsultingContext ? consultingUnidades : [];
  }, [
    isFlowEnabled,
    consultingUnidades,
    form.isRescheduling,
    hadEnteredConsultingOnMount,
  ]);

  const unitsMap = useMemo(() => {
    const map = new Map<string, { codUnidade: number; nome: string; endereco: string }>();
    for (const unit of data?.unidades ?? []) {
      const value = unit.nomeLimpo?.toLowerCase().replaceAll(/\s+/g, "-") ?? "";
      const endereco = [unit.logradouro, unit.complemento, unit.bairro, unit.Cidade.nome]
        .filter(Boolean)
        .join(" - ");
      map.set(value, {
        codUnidade: unit.codUnidade,
        nome: unit.nomeLimpo ?? "",
        endereco,
      });
    }
    return map;
  }, [data]);

  const currentDefaultValue = useMemo(() => {
    for (const [value, unit] of unitsMap) {
      if (unit.codUnidade === form.selectedLocationCode) return value;
    }
    return undefined;
  }, [unitsMap, form.selectedLocationCode]);

  const handleUnitChange = (value: string): void => {
    const unit = unitsMap.get(value);
    if (unit) {
      setForm((prev) => ({
        ...prev,
        selectedLocationCode: unit.codUnidade,
        selectedLocationName: unit.nome,
        selectedLocationAddress: unit.endereco,
      }));
    }
  };

  const handleContinue = (): void => {
    if (isConsultingEligible) {
      const metadataRows = priorConsultingData?.onboarding_visit_metadata;
      const previousAnswers: unknown = metadataRows?.[0]?.metadata;

      // No reagendamento, reaproveita as respostas já enviadas e pula as
      // perguntas, indo direto para a escolha de novo dia/horário.
      if (form.isRescheduling && previousAnswers) {
        setConsulting(parseConsultingAnswers(previousAnswers));

        void navigate({
          to: "/onboarding/welcome-meeting/schedule",
          search: {
            consulting: true,
            codUnidade: form.selectedLocationCode,
            nome: form.selectedLocationName,
            endereco: form.selectedLocationAddress,
          },
        });
        return;
      }

      // Marca a entrada no fluxo de consultoria para revelar o selo das
      // unidades apenas quando o membro voltar para trocar de unidade.
      setForm((prev) => ({ ...prev, hasEnteredConsulting: true }));
      void navigate({ to: "/onboarding/consulting/intro" });
      return;
    }

    void navigate({
      to: "/onboarding/welcome-meeting/schedule",
      search: {
        codUnidade: form.selectedLocationCode,
        nome: form.selectedLocationName,
        endereco: form.selectedLocationAddress,
      },
    });
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={2 / 3} />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-4">
          <CardSelectRoot
            name="location-select"
            onChange={handleUnitChange}
            defaultValue={currentDefaultValue}
          >
            <CardSelectTitle>
              Em qual unidade você deseja fazer a reunião?
            </CardSelectTitle>
            <CardSelectDescription>
              Selecione uma opção na lista abaixo
            </CardSelectDescription>
            <LocationCardSelect
              showRoomTypeFilter={false}
              allowVirtualSchedules={false}
              consultingUnitIds={consultingUnitIds}
              excludedUnitIds={excludedUnitIds}
            />
          </CardSelectRoot>
        </div>

        <WelcomeMeetingFooter
          onSubmit={handleContinue}
          disabled={form.selectedLocationCode === 0}
        />
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/location")({
  validateSearch: welcomeMeetingLocationSearchSchema,
  component: WelcomeMeetingUnitPage,
});
